import { useMemo } from "react";

import { useTheme } from "@mui/material";
import { type EChartsOption } from "echarts";
import ECharts from "echarts-for-react";

import { useColors } from "../../../CloudAnalytics/renderers/ECharts/useColors";
import { useTopTenClusters } from "../../hooks";
import ChartCard from "../ClusterDetail/Charts/ChartCard";
import {
  formatMoney,
  formatTimestamp,
  formatTimestampAxis,
  getCommonChartOptions,
} from "../ClusterDetail/Charts/utils";

interface ChartDataItem {
  name: string;
  value: number;
  date: string;
  formattedDate: string;
}

const ClusterCostChart = () => {
  const theme = useTheme();

  const { topTenClusters, loading } = useTopTenClusters();
  const { colors } = useColors(topTenClusters?.length ?? 0, true);

  const chartData = useMemo(() => {
    const data: ChartDataItem[] = [];

    topTenClusters?.forEach((cluster) => {
      cluster.costs.forEach((item) => {
        data.push({
          name: cluster.clusterName,
          date: item.time,
          value: item.cost,
          formattedDate: formatTimestampAxis(formatTimestamp(item.time)),
        });
      });
    });

    return data.sort((a, b) => {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();

      return dateA - dateB;
    });
  }, [topTenClusters]);

  const uniqueDates = Array.from(new Set(chartData.map((i) => i.formattedDate)));
  const uniqueClusterNames = topTenClusters?.map((i) => i.clusterName) ?? [];

  const sortedClusterNames = uniqueClusterNames.toSorted((a, b) => {
    const calculateTotal = (name: string) =>
      chartData.filter((i) => i.name === name).reduce((sum, item) => sum + item.value, 0);

    return calculateTotal(a) - calculateTotal(b);
  });

  const chartOption: EChartsOption = {
    ...getCommonChartOptions(theme, {
      grid: {
        top: 10,
        left: 50,
        right: 10,
        bottom: 50,
      },
      tooltip: {
        trigger: "item",
        valueFormatter: (v) => formatMoney(v?.toString() ?? "", 3),
      },
      yAxis: {
        type: "value",
        axisLabel: {
          formatter: (value) => formatMoney(value.toString(), 3),
        },
      },
    }),
    color: colors,
    xAxis: {
      data: uniqueDates,
    },
    series: sortedClusterNames.map((name) => ({
      name,
      type: "bar",
      stack: "total",
      barWidth: "40%",
      emphasis: {
        focus: "series",
      },
      data: uniqueDates.map((date) => chartData.find((i) => i.name === name && i.formattedDate === date)?.value ?? 0),
    })),
  };

  return (
    <ChartCard title="Top 10 clusters by compute cost" height={310} empty={!chartData?.length} loading={loading}>
      <ECharts
        option={chartOption}
        lazyUpdate
        style={{
          height: "100%",
          width: "100%",
        }}
      />
    </ChartCard>
  );
};

export default ClusterCostChart;
